import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RevealHeading } from "@/components/Reveal";
import { Upload, X, Check, Loader2, Info } from "lucide-react";
import { BuildSpec, emptyBuild, summarizeBuildSpec } from "@/components/BuildSpec";

const CONFIG = {
  tuning: {
    label: "Tuning",
    kicker: "// ECU Calibration & Power",
    heading: ["Tuning", "Request"],
    blurb: "Custom ECU calibration, flex fuel, and transmission tuning. Tell us your platform and power goals.",
    img: "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/cd4b064a2b12666ff413ab9f89c276f922b3293aad9ac44794eb2966d63e4813.jpeg",
  },
  fabrication: {
    label: "Fabrication",
    kicker: "// Custom Metal & Welding",
    heading: ["Fabrication", "Request"],
    blurb: "Custom exhausts, intercooler piping and one-off parts. Describe the build and share reference photos.",
    img: "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/06919d5fb5660f9da2e5196f33c979bc9163cd31765819db98682d8d170ea42d.jpeg",
  },
};

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
      {label} {required && <span className="text-neon">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-matte border border-[#222] px-4 py-3 font-mono text-sm text-[#FAFAFA] placeholder:text-[#555] outline-none focus:border-neon focus:ring-1 focus:ring-neon transition-colors rounded-none";

export default function RequestForm({ type }) {
  const cfg = CONFIG[type];
  const isTuning = type === "tuning";
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "",
    description: "",
  });
  const [files, setFiles] = useState([]); // {path, filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Tuning pricing estimator
  const [tuningOpts, setTuningOpts] = useState([]);
  const [baseId, setBaseId] = useState("");
  const [addonIds, setAddonIds] = useState([]);
  const [estimate, setEstimate] = useState(0);
  const [buildSpec, setBuildSpec] = useState(emptyBuild);

  useEffect(() => {
    if (!isTuning) return;
    api.get("/tuning-options")
      .then(({ data }) => setTuningOpts(data.filter((o) => o.active !== false)))
      .catch(() => {});
  }, [isTuning]);

  const bases = tuningOpts.filter((o) => o.category === "base").sort((a, b) => a.order - b.order);
  const addons = tuningOpts.filter((o) => o.category === "addon").sort((a, b) => a.order - b.order);

  // A base tune must always be selected — default to the primary (first) base once options load.
  useEffect(() => {
    if (isTuning && bases.length > 0 && !baseId) setBaseId(bases[0].id);
  }, [isTuning, bases, baseId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleAddon = (id) =>
    setAddonIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    const byId = Object.fromEntries(tuningOpts.map((o) => [o.id, o]));
    let total = baseId && byId[baseId] ? byId[baseId].price : 0;
    addonIds.forEach((id) => { if (byId[id]) total += byId[id].price; });
    setEstimate(total);
  }, [baseId, addonIds, tuningOpts]);

  const onUpload = async (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const file of chosen) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (path) => setFiles((prev) => prev.filter((f) => f.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Please fill in name, email and description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/requests", {
        type,
        ...form,
        attachments: files.map((f) => f.path),
        ...(isTuning ? { tuning_base: baseId, tuning_addons: addonIds, build_spec: summarizeBuildSpec(buildSpec) } : {}),
      });
      setDone(true);
      toast.success("Request submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-void min-h-screen" data-testid={`${type}-request-page`}>
      <Navbar />

      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-16 md:pt-20">
        {/* Left visual */}
        <div className="relative border-b lg:border-b-0 lg:border-r border-[#222] overflow-hidden min-h-[40vh] lg:min-h-0">
          <img src={cfg.img} alt={cfg.label} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-void/60" />
          <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-14">
            <div className="text-xs uppercase tracking-[0.3em] text-neon mb-5">{cfg.kicker}</div>
            <h1 className="font-display uppercase text-6xl md:text-7xl lg:text-8xl leading-[0.82] tracking-tight">
              <RevealHeading lines={cfg.heading} />
            </h1>
            <p className="mt-6 font-mono text-sm text-[#9CA3AF] max-w-sm">{cfg.blurb}</p>
          </div>
        </div>

        {/* Right form */}
        <div className="p-6 md:p-14 flex items-center">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full text-center py-20"
              data-testid="request-success"
            >
              <div className="w-16 h-16 bg-neon flex items-center justify-center mx-auto mb-8">
                <Check size={32} className="text-void" />
              </div>
              <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Request Received</h2>
              <p className="mt-4 font-mono text-sm text-[#9CA3AF] max-w-md mx-auto">
                Thanks — your {cfg.label.toLowerCase()} request is in. Our team will reach out to {form.email} shortly.
              </p>
              {isTuning && estimate > 0 && (
                <p data-testid="success-estimate" className="mt-4 font-mono text-sm text-[#9CA3AF]">
                  Your non-binding estimate: <span className="text-neon font-bold">${estimate.toLocaleString()}</span>
                </p>
              )}
            </motion.div>
          ) : (
            <form onSubmit={submit} className="w-full max-w-xl mx-auto space-y-6" data-testid={`${type}-form`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <input data-testid="field-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="John Doe" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </Field>
              </div>
              <Field label="Phone">
                <input data-testid="field-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Make">
                  <input data-testid="field-make" className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Nissan" />
                </Field>
                <Field label="Model">
                  <input data-testid="field-model" className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="GT-R" />
                </Field>
                <Field label="Year">
                  <input data-testid="field-year" className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2018" />
                </Field>
              </div>

              {isTuning && (
                <div data-testid="tuning-estimator" className="border border-[#222] bg-matte">
                  <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
                    <span className="font-display uppercase text-xl tracking-tight">Tuning <span className="text-neon">Estimator</span></span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Non-binding estimate</span>
                  </div>

                  <div className="p-5 space-y-6">
                    {/* Base tune */}
                    <div>
                      <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">Base Tune</span>
                      <div className="space-y-2">
                        {bases.map((o) => (
                          <button
                            type="button"
                            key={o.id}
                            data-testid={`base-tune-${o.id}`}
                            onClick={() => setBaseId(o.id)}
                            className={`w-full flex items-center justify-between gap-4 border px-4 py-3 text-left transition-colors ${baseId === o.id ? "border-neon bg-neon/5" : "border-[#222] hover:border-[#444]"}`}
                          >
                            <span className="flex items-center gap-3 min-w-0">
                              <span className={`w-4 h-4 rounded-full border shrink-0 ${baseId === o.id ? "border-neon bg-neon" : "border-[#555]"}`} />
                              <span className="min-w-0">
                                <span title={o.label} className="block font-grotesk font-bold text-sm text-[#FAFAFA] md:truncate">{o.label}</span>
                                {o.description && <span title={o.description} className="block font-mono text-[11px] text-[#9CA3AF] md:truncate">{o.description}</span>}
                              </span>
                            </span>
                            <span className="font-display text-lg text-neon shrink-0">${o.price.toLocaleString()}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Add-ons */}
                    <div>
                      <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">Add-ons</span>
                      <div className="space-y-2">
                        {addons.map((o) => {
                          const on = addonIds.includes(o.id);
                          return (
                            <button
                              type="button"
                              key={o.id}
                              data-testid={`addon-${o.id}`}
                              onClick={() => toggleAddon(o.id)}
                              className={`w-full flex items-center justify-between gap-4 border px-4 py-3 text-left transition-colors ${on ? "border-neon bg-neon/5" : "border-[#222] hover:border-[#444]"}`}
                            >
                              <span className="flex items-center gap-3 min-w-0">
                                <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${on ? "border-neon bg-neon" : "border-[#555]"}`}>
                                  {on && <Check size={12} className="text-void" />}
                                </span>
                                <span className="min-w-0">
                                  <span title={o.label} className="block font-grotesk font-bold text-sm text-[#FAFAFA] md:truncate">{o.label}</span>
                                  {o.description && <span title={o.description} className="block font-mono text-[11px] text-[#9CA3AF] md:truncate">{o.description}</span>}
                                </span>
                              </span>
                              <span className={`font-display text-lg shrink-0 ${o.price === 0 ? "text-[#9CA3AF] text-sm" : "text-neon"}`}>
                                {o.price === 0 ? "Included" : `+$${o.price.toLocaleString()}`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Running total */}
                    <div className="flex items-center justify-between border-t-2 border-neon pt-4">
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Estimated Total</span>
                      <span data-testid="tuning-estimate-total" className="font-display text-3xl text-neon">${estimate.toLocaleString()}</span>
                    </div>

                    {/* Dyno note */}
                    <div className="flex items-start gap-2.5 border border-[#222] bg-void px-4 py-3">
                      <Info size={15} className="text-neon mt-0.5 shrink-0" />
                      <p className="font-mono text-[11px] leading-relaxed text-[#9CA3AF]">
                        Tuning is done remotely (e-tune) — there is <span className="text-[#FAFAFA]">no on-site dyno</span>. Dyno time is only needed if you book a shop that has one. Estimate is a starting figure and is not a final quote.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isTuning && <BuildSpec value={buildSpec} onChange={setBuildSpec} />}

              <Field label={`${cfg.label} Details`} required>
                <textarea data-testid="field-description" rows={5} className={inputCls} value={form.description} onChange={set("description")} placeholder="Tell us about your car, goals, current mods…" />
              </Field>

              {/* Uploads */}
              <div>
                <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Reference Photos</span>
                <label
                  data-testid="upload-label"
                  className="flex items-center gap-3 border border-dashed border-[#333] px-4 py-4 cursor-pointer hover:border-neon transition-colors"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin text-neon" /> : <Upload size={18} className="text-neon" />}
                  <span className="font-mono text-sm text-[#9CA3AF]">{uploading ? "Uploading…" : "Click to upload photos"}</span>
                  <input data-testid="field-files" type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {files.map((f) => (
                      <span key={f.path} className="flex items-center gap-2 bg-matte border border-[#222] px-3 py-2 text-xs font-mono text-[#FAFAFA]">
                        {f.filename}
                        <button type="button" onClick={() => removeFile(f.path)} data-testid={`remove-file-${f.path}`} className="text-[#9CA3AF] hover:text-neon">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                data-testid="submit-request-btn"
                disabled={submitting}
                className="w-full bg-neon text-void font-bold uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 hover:bg-[#FAFAFA] transition-colors disabled:opacity-50"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : `Submit ${cfg.label} Request`}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
